import React, { useState } from "react";
import { toast } from "react-toastify";
import { sendToAquarius } from "../../../../../apis/sendToAquarius";

type Props = {
  staff: boolean;
  documentId: number;
  sentAt?: string | null;
  isSent?: boolean;
  onSent?: (documentId: number, docId?: string, sentAt?: string) => void;
  sendLabel?: string;
  sendingLabel?: string;
  errorLabel?: string;
  sentLabel?: string;
  locale?: string;
};

export const formatAquariusSentAt = (
  value?: string | null,
  locale = "en-US"
) => {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const SendToAquariusButton = ({
  staff,
  documentId,
  sentAt,
  isSent,
  onSent,
  sendLabel = "Send to Aquarius",
  sendingLabel = "Sending...",
  errorLabel = "Could not send this document to Aquarius.",
  sentLabel = "Sent",
  locale = "en-US",
}: Props) => {
  const [sending, setSending] = useState(false);

  if (!staff) {
    return null;
  }

  const handleClick = async () => {
    if (sending || documentId == null) {
      return;
    }

    setSending(true);
    try {
      const response = await sendToAquarius(documentId);
      const nextDocId =
        typeof response?.doc_id === "string" ? response.doc_id : undefined;
      const nextSentAt =
        response?.sent_at != null ? String(response.sent_at) : undefined;
      onSent?.(documentId, nextDocId, nextSentAt);
      toast.success(response?.message || sendLabel);
    } catch (error: any) {
      const data = error?.response?.data;
      const message =
        (typeof data?.error === "string" && data.error) ||
        (typeof data?.detail === "string" && data.detail) ||
        errorLabel;
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  const formattedSentAt = formatAquariusSentAt(sentAt, locale);

  return (
    <>
      {(isSent || sentAt) && (
        <span className="aquarius-sent-at">
          {sentLabel}
          {formattedSentAt ? `: ${formattedSentAt}` : ""}
        </span>
      )}
      <button
        type="button"
        className="aquarius-button"
        onClick={handleClick}
        disabled={sending}
      >
        {sending ? sendingLabel : sendLabel}
      </button>
    </>
  );
};

export default SendToAquariusButton;
